/**
 * CrmMappingsTable — per-entity CRM sync tallies for the SuperAdmin → Operations → CRM subtab.
 *
 * Renders one row per entity type (user / organization / business / vendor / invoice)
 * showing total live cogslog rows, how many are synced to Twenty, how many are still
 * un-mapped, how many have failed inside the last 24 h, and when the most recent
 * successful sync landed. The Un-mapped and Failing cells are rendered as inline
 * buttons — clicking a non-zero cell fires `onRowClick(entity_type, 'unmapped'|'failing')`
 * so the parent can open a drilldown drawer with the affected rows.
 *
 * Visual encodings (all via Seedling CSS tokens):
 *   - Failing count > 0        → warning color (var(--warning))
 *   - Failing count > 5        → error color   (var(--error))
 *   - Totals / numeric cells   → mono font var(--font-m)
 *   - Empty tally set overall  → .empty-state placeholder in place of the table
 *
 * @param {Object}   props
 * @param {Object}   props.tallies      - Per-entity tally map, keyed by entity type
 *                                        ('user' | 'organization' | 'business' | 'vendor').
 *                                        Each value: { total, synced, unmapped, failing, last_sync_at }.
 * @param {Function} props.onRowClick   - (entityType, 'unmapped'|'failing') => void. Fired when the
 *                                        operator clicks a non-zero Un-mapped or Failing cell.
 * @returns {JSX.Element}
 */
import React from 'react';

// Fixed row order — matches backfill/reconcile ordering in routes/admin_crm.py.
const ENTITY_ROWS = [
  { key: 'user', label: 'Users' },
  { key: 'organization', label: 'Organizations' },
  { key: 'business', label: 'Businesses' },
  { key: 'vendor', label: 'Vendors' },
  // 'invoice' row removed 2026-08-26 — invoices are vendor bills, not
  // sales-pipeline events. Payments → opportunities mapping tracked in
  // GH #233.
];

const MS_PER_MIN = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MIN;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/**
 * Coerce a possibly-undefined tally entry into a fully populated object.
 * @param {Object} [t] - Raw tally entry from props.tallies.
 * @returns {{total:number, synced:number, unmapped:number, failing:number, last_sync_at:?string}}
 */
function normalizeTally(t) {
  return {
    total: Number((t && t.total) || 0),
    synced: Number((t && t.synced) || 0),
    unmapped: Number((t && t.unmapped) || 0),
    failing: Number((t && t.failing) || 0),
    last_sync_at: (t && t.last_sync_at) || null,
  };
}

/**
 * Format an ISO timestamp as a compact relative time string.
 * Falls back to a localized date string for anything older than a week.
 * @param {?string} iso - ISO 8601 timestamp, or null/undefined.
 * @returns {string} e.g. "just now", "3m ago", "2h ago", "yesterday", "3d ago", or a date.
 */
function formatRelativeTime(iso) {
  if (!iso) return 'never';
  const then = new Date(iso);
  const t = then.getTime();
  if (Number.isNaN(t)) return 'never';
  const diff = Date.now() - t;
  if (diff < 0) return 'just now';
  if (diff < MS_PER_MIN) return 'just now';
  if (diff < MS_PER_HOUR) {
    const m = Math.floor(diff / MS_PER_MIN);
    return `${m}m ago`;
  }
  if (diff < MS_PER_DAY) {
    const h = Math.floor(diff / MS_PER_HOUR);
    return `${h}h ago`;
  }
  const days = Math.floor(diff / MS_PER_DAY);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return then.toLocaleDateString();
}

/**
 * Pick the visual severity class for the Failing cell.
 * @param {number} failing - Count of rows failing in the last 24 h.
 * @returns {string} Suffix class name portion — '', '--warn', or '--error'.
 */
function failingSeverityClass(failing) {
  if (failing > 5) return 'crm-mappings-table__failing--error';
  if (failing > 0) return 'crm-mappings-table__failing--warn';
  return '';
}

function CrmMappingsTable({ tallies, onRowClick }) {
  const rows = ENTITY_ROWS.map((row) => ({
    ...row,
    tally: normalizeTally(tallies && tallies[row.key]),
  }));

  // "Empty" = every entity type has zero of everything (nothing to show yet).
  const allZero = rows.every((r) => {
    const t = r.tally;
    return t.total === 0 && t.synced === 0 && t.unmapped === 0 && t.failing === 0;
  });

  if (allZero) {
    return (
      <div
        className="empty-state"
        role="status"
        data-testid="crm-mappings-table-empty"
      >
        <h3 className="empty-state__title">No CRM syncs yet</h3>
        <p className="empty-state__description">
          Click Backfill to seed the CRM with existing cogslog records.
        </p>
      </div>
    );
  }

  /**
   * Fire onRowClick. Only wired to cells that already render as clickable
   * buttons — the render logic above decides whether a cell is interactive
   * based on column-specific rules (failing gated on count > 0; unmapped
   * gated on total > 0, since the drilldown fetches the authoritative list
   * server-side).
   *
   * @param {string} entityType - one of user|organization|business|vendor|invoice
   * @param {'unmapped'|'failing'} column - which drilldown column was clicked
   */
  const handleCellClick = (entityType, column) => {
    if (typeof onRowClick !== 'function') return;
    onRowClick(entityType, column);
  };

  return (
    <div className="tbl-wrap crm-mappings-table" data-testid="crm-mappings-table">
      <table>
        <thead>
          <tr>
            <th scope="col">Entity</th>
            <th scope="col" className="crm-mappings-table__num-header">Total</th>
            <th scope="col" className="crm-mappings-table__num-header">Synced</th>
            <th scope="col" className="crm-mappings-table__num-header">Un-mapped</th>
            <th scope="col" className="crm-mappings-table__num-header">Failing (24h)</th>
            <th scope="col">Last sync</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ key, label, tally }) => {
            const failingClass = failingSeverityClass(tally.failing);
            // Un-mapped cell is clickable whenever there is any live row for
            // this entity type — the drilldown drawer fetches the authoritative
            // list via GET /crm/mappings?entity_type=X&status=unmapped, so a
            // known-zero tally.unmapped from the summary shouldn't gate the
            // click (the summary currently reports 0 for every entity type;
            // see SuperAdminCrm.buildTallies). If nothing is live we keep the
            // cell inert so the operator isn't offered an empty drawer.
            const unmappedInteractive = tally.total > 0;
            const failingInteractive = tally.failing > 0;
            return (
              <tr
                key={key}
                data-testid={`crm-mappings-row-${key}`}
              >
                <th scope="row" className="crm-mappings-table__label">{label}</th>
                <td
                  className="crm-mappings-table__num"
                  data-testid={`crm-mappings-cell-${key}-total`}
                >
                  {tally.total.toLocaleString()}
                </td>
                <td
                  className="crm-mappings-table__num"
                  data-testid={`crm-mappings-cell-${key}-synced`}
                >
                  {tally.synced.toLocaleString()}
                </td>
                <td
                  className="crm-mappings-table__num"
                  data-testid={`crm-mappings-cell-${key}-unmapped`}
                >
                  {unmappedInteractive ? (
                    <button
                      type="button"
                      className="crm-mappings-table__cell-btn"
                      onClick={() => handleCellClick(key, 'unmapped')}
                      aria-label={`View un-mapped ${label.toLowerCase()}`}
                    >
                      {tally.unmapped.toLocaleString()}
                    </button>
                  ) : (
                    <span className="crm-mappings-table__cell-zero">0</span>
                  )}
                </td>
                <td
                  className={`crm-mappings-table__num crm-mappings-table__failing ${failingClass}`.trim()}
                  data-testid={`crm-mappings-cell-${key}-failing`}
                >
                  {failingInteractive ? (
                    <button
                      type="button"
                      className={`crm-mappings-table__cell-btn ${failingClass}`.trim()}
                      onClick={() => handleCellClick(key, 'failing')}
                      aria-label={`View ${tally.failing} failing ${label.toLowerCase()} (last 24 hours)`}
                    >
                      {tally.failing.toLocaleString()}
                    </button>
                  ) : (
                    <span className="crm-mappings-table__cell-zero">0</span>
                  )}
                </td>
                <td
                  className="crm-mappings-table__last-sync"
                  data-testid={`crm-mappings-cell-${key}-last-sync`}
                  title={tally.last_sync_at || 'never'}
                >
                  {formatRelativeTime(tally.last_sync_at)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default CrmMappingsTable;
